/**
 * Leads service — the CRM funnel's intake stage (Airtable `Leads` table).
 *
 * React-on-Airtable: this file owns the Leads table id + field names and returns normalized,
 * camelCase lead objects so CRM components never touch raw Airtable shapes or field strings.
 *
 * Airtable rules honored: no fields[] on the list query (the list needs the `Latest Interaction`
 * rollup + `Rating`/`Days Since` formulas, which vanish silently under fields[]); select writes use
 * { typecast:true }; the `Opportunity` link is filtered/written by record id.
 *
 * Lifecycle: Lead Status New → Contacted → Engaged → Qualified → Disqualified, plus a `Converted?`
 * checkbox (watched by the existing "lead converted" Zapier automations — left in place; see
 * docs/MIGRATION_DERIVATIONS.md). `Latest Interaction` / `Rating` / `Days Since Last Interaction`
 * are read-only Airtable formulas/rollups driven by the linked Interactions.
 */

import { apiGet, apiGetAll, apiGetOne, apiPatch, apiPost } from './_api.js'

const TBL_LEAD = 'tblMS59GDDKyq78us'

export const LEAD_STATUSES = ['New', 'Contacted', 'Engaged', 'Qualified', 'Disqualified']

export const DISQUALIFY_REASONS = [
  'Budget Issues', 'Timeline Issues', 'Not a Good Fit', 'Unresponsive',
  'Chose a Competitor', 'Not Interested', 'Abandoned Project', 'Duplicate Lead', 'Other',
]

/**
 * Close-out taxonomy — the app's own reason list for the unified "Close out lead" flow
 * (CrmLeadDetailModal's CloseOutBox). Each entry maps to the nearest Airtable
 * `Disqualification Reason` option; the precise key + note + dispositions are stored on the
 * lead's `lead_judgment` row (database/147_lead_closeout.sql), so nothing is lost if the
 * Airtable option is coarser than our reason.
 *
 * Flags drive which dispositions the CloseOutBox offers:
 *  - decline:      offer the templated decline message (real prospects we're turning down)
 *  - contacts:     offer one-click "Add to Contacts directory" (partner/vendor closes)
 *  - slowBurnHint: offer "keep as slow burn instead" (timeline closes that aren't really dead)
 *  - fallback:     Airtable reason to retry with if the primary option doesn't exist yet
 *                  (typecast can only create new select options when the token has permission)
 */
export const CLOSE_REASONS = [
  { key: 'went_cold',      label: 'They went unresponsive / cold',           airtable: 'Unresponsive', slowBurnHint: true },
  { key: 'budget',         label: "Budget doesn't support our fee range",    airtable: 'Budget Issues', decline: true },
  { key: 'timeline',       label: 'Timeline never materialized',             airtable: 'Timeline Issues', slowBurnHint: true },
  { key: 'not_a_fit',      label: 'Not a fit (project or client)',           airtable: 'Not a Good Fit', decline: true },
  { key: 'competitor',     label: 'Chose a competitor',                      airtable: 'Chose a Competitor' },
  { key: 'not_interested', label: 'Decided not to proceed',                  airtable: 'Not Interested' },
  { key: 'abandoned',      label: 'Project abandoned',                       airtable: 'Abandoned Project' },
  { key: 'not_a_client',   label: 'Not a prospective client (partner / vendor / other)', airtable: 'Not a Prospective Client', fallback: 'Other', contacts: true },
  { key: 'duplicate',      label: 'Duplicate lead',                          airtable: 'Duplicate Lead' },
  { key: 'other',          label: 'Other',                                   airtable: 'Other' },
]

export function closeReasonByKey(key) {
  return CLOSE_REASONS.find(r => r.key === key) || null
}

export const LEAD_SOURCES = [
  'Inbound Inquiry', 'Referral', 'Networking', 'Cold Outreach', 'Marketing Campaign',
  'Web Search', 'Social Media', 'Event Attendance', 'Existing Database', 'Other',
]

export const SALUTATIONS = ['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Prof.']

// Lead Source sub-option lists (branching in the new-lead wizard).
export const INBOUND_OPTIONS = ['Website Form', 'Email', 'Phone Call', 'Social Media', 'Other']
export const EXISTING_DB_OPTIONS = ['Past Client', 'Re-engaged Contact', 'General Contact Update']

/* ── Normalize ───────────────────────────────────────────────────────────── */
function normLead(r) {
  const f = (r && r.fields) || {}
  const first = arr => (Array.isArray(f[arr]) ? f[arr][0] : null) || null
  return {
    id: r.id,
    name: f['Name'] || `${f['First Name'] || ''} ${f['Last Name'] || ''}`.trim(),
    firstName: f['First Name'] || '',
    lastName: f['Last Name'] || '',
    salutation: f['Salutation'] || '',
    organization: f['Organization'] || '',
    email: f['Email'] || '',
    phone: f['Phone Number'] || '',
    status: f['Lead Status'] || '',
    source: f['Lead Source'] || '',
    rating: f['Rating'] || '',                                  // read-only formula (🔥/🌡️/❄️/⌛)
    // read-only formula. Airtable returns a plain number normally, but when it can't compute
    // (e.g. zero interactions logged yet) it returns { specialValue: 'NaN' } instead of null —
    // guard so callers only ever see a finite number or null (never that raw object).
    daysSinceContact: (v => typeof v === 'number' && isFinite(v) ? v : null)(f['Days Since Last Interaction']),
    latestInteraction: f['Latest Interaction'] || null,         // read-only rollup (date)
    converted: !!f['Converted?'],
    disqualReason: f['Disqualification Reason'] || '',
    description: f['Description'] || '',
    street: f['Street Address'] || '',
    city: f['City'] || '',
    state: f['State'] || '',
    zip: f['Zip Code'] || '',
    apn: f['APN'] || '',
    website: f['Website'] || '',
    sourceDetails: f['Lead Source Additional Details'] || '',
    additionalContact: f['Additional Contact'] || '',
    referredByName: f['Referred By Name'] || '',
    referredById: first('Referred By'),
    opportunityId: first('Opportunity'),
    managingMemberId: first('Managing Team Member'),
    interactionIds: Array.isArray(f['Interactions']) ? f['Interactions'] : [],
    researchIds: Array.isArray(f['Research']) ? f['Research'] : [],
    contactIds: Array.isArray(f['Contacts']) ? f['Contacts'] : [],
    createdTime: f['Created'] || null,
    // Pre-existing fields, previously unused by this service — an external automation posts to
    // #aj_biz-dev and writes the resulting thread ts back here when a lead is created.
    slackThreadTs: f['Slack Thread Timestamp'] || '',
    raw: f,
  }
}

/* ── Field mapping for writes (clean key → Airtable field) ───────────────── */
const WRITE_MAP = {
  firstName: 'First Name', lastName: 'Last Name', salutation: 'Salutation',
  organization: 'Organization', email: 'Email', phone: 'Phone Number',
  status: 'Lead Status', source: 'Lead Source', description: 'Description',
  street: 'Street Address', city: 'City', state: 'State', zip: 'Zip Code',
  apn: 'APN', website: 'Website', disqualReason: 'Disqualification Reason',
  inboundOption: 'Inbound Inquiry Options', existingDbOption: 'Existing Database Option',
  otherSource: 'Other Lead Source', sourceDetails: 'Lead Source Additional Details',
  additionalContact: 'Additional Contact',
}

function toFields(patch) {
  const out = {}
  for (const [k, v] of Object.entries(patch || {})) {
    if (k in WRITE_MAP) out[WRITE_MAP[k]] = v
    else if (k === 'converted') out['Converted?'] = !!v
    else if (k === 'referredById') out['Referred By'] = v ? [v] : []
    else if (k === 'opportunityId') out['Opportunity'] = v ? [v] : []
    else if (k === 'managingMemberId') out['Managing Team Member'] = v ? [v] : []
    else if (k === 'contactIds') out['Contacts'] = Array.isArray(v) ? v : []
  }
  return out
}

/* ── Reads ───────────────────────────────────────────────────────────────── */

/**
 * List leads, optionally filtered by status and/or source. Sorted client-side
 * (Rating is a formula; server-side sort on it times out).
 * @param {{status?: string, source?: string}} [opts]
 * @returns {Promise<Array>} normalized lead objects
 */
export async function getLeads(opts = {}) {
  const clauses = []
  if (opts.status) clauses.push(`{Lead Status}='${opts.status}'`)
  if (opts.source) clauses.push(`{Lead Source}='${opts.source}'`)
  const formula = clauses.length === 1 ? clauses[0] : clauses.length > 1 ? `AND(${clauses.join(',')})` : ''
  const qs = formula ? 'filterByFormula=' + encodeURIComponent(formula) : ''
  const records = await apiGetAll(TBL_LEAD, qs)
  return records.map(normLead)
}

/** Fetch one lead (full record). */
export async function getLeadDetail(leadId) {
  const r = await apiGetOne(TBL_LEAD, leadId)
  return r ? normLead(r) : null
}

/* Airtable writes return { error } on failure (the proxy passes the body through verbatim)
   instead of throwing — surface it so a bad write shows up as a real error instead of
   silently doing nothing. */
function throwOnError(res, what) {
  if (res && res.error) {
    const msg = (res.error && (res.error.message || res.error.type)) || ('Could not save the ' + what)
    throw new Error(msg)
  }
  return res
}

/* ── Writes ──────────────────────────────────────────────────────────────── */

export async function createLead(patch) {
  const res = throwOnError(await apiPost(TBL_LEAD, toFields(patch), { typecast: true }), 'lead')
  return res && res.id ? normLead(res) : null
}

export async function updateLead(leadId, patch) {
  const res = throwOnError(await apiPatch(TBL_LEAD, leadId, toFields(patch), { typecast: true }), 'lead')
  return res && res.id ? normLead(res) : null
}

/** Mark a lead Qualified. */
export function qualifyLead(leadId) {
  return updateLead(leadId, { status: 'Qualified' })
}

/** Mark a lead Disqualified with a reason (from DISQUALIFY_REASONS). */
export function disqualifyLead(leadId, reason) {
  return updateLead(leadId, { status: 'Disqualified', disqualReason: reason || 'Other' })
}

/**
 * Disqualify with a reason that may not exist as an Airtable option yet. typecast:true creates
 * the option when the token has creator permissions; if Airtable rejects the write instead,
 * retry once with the coarser fallback so the close-out never fails on taxonomy alone.
 */
export async function disqualifyLeadSafe(leadId, reason, fallback = 'Other') {
  try { return await disqualifyLead(leadId, reason) }
  catch (e) {
    if (fallback && fallback !== reason) return disqualifyLead(leadId, fallback)
    throw e
  }
}

/**
 * Link a lead to its Opportunity (a pursuit-stage Project) and flag it converted.
 * The Project itself is created by services/opportunities.createOpportunity (Step 4 wires the modal).
 * Leaves the existing Zapier automations (which watch `Converted?`) in place.
 */
export function convertLead(leadId, { projectId } = {}) {
  const patch = { converted: true, status: 'Qualified' }
  if (projectId) patch.opportunityId = projectId
  return updateLead(leadId, patch)
}
